import type { TypesVersionInOut } from '@festina/configuration-general-types/configuration'
import { useCreateTypesVersion } from '@festina-life/api'
import { assertNotEmpty, type Optional } from '@festina-life/core'
import { useCallback } from 'react'

import { loadTypedDataFromFile } from '../../components/branchCreator/helpers'
import {
  type BranchTypesInput,
  isTypesVersionInOut,
  type LayerTypesVersionsInOut,
} from './types'

export const useCreateTypesVersionFromFile = () => {
  const { mutateAsync: createTypesVersion, isPending } = useCreateTypesVersion()

  const loadTypesPackage = useCallback(async (values: BranchTypesInput) => {
    if (!values.typeVersionFile.length) {
      return
    }

    const file = values.typeVersionFile.at(0)
    assertNotEmpty(file)

    return await loadTypedDataFromFile<
      TypesVersionInOut | LayerTypesVersionsInOut
    >(file, ['TypesVersionInOut', 'LayerTypesVersionsInOut'])
  }, [])

  const mutateAsync = useCallback(
    async (
      values: BranchTypesInput,
    ): Promise<Optional<string | [Optional<string>, Optional<string>][]>> => {
      const typesPackage = await loadTypesPackage(values)

      if (!typesPackage) {
        return
      }

      if (isTypesVersionInOut(typesPackage)) {
        return await createTypesVersion({
          requestBody: {
            ...typesPackage,
            description: values.typesVersionDescription,
            name: values.typesVersionName,
          },
        })
      } else {
        const typesForLayers = typesPackage.layerTypesVersions
        const createAllVersionsResult = await Promise.all(
          typesForLayers.map(
            async (layerTypesVersion) =>
              await createTypesVersion({
                requestBody: {
                  ...layerTypesVersion.typesVersion,
                  description: values.typesVersionDescription,
                  name: values.typesVersionName,
                },
              }),
          ),
        )

        return createAllVersionsResult.map(
          (result, index) => [typesForLayers[index].layer, result] as const,
        )
      }
    },
    [createTypesVersion, loadTypesPackage],
  )

  return { mutateAsync, isPending }
}
